import type { Incident, IncidentUpdate } from "@statuspage/shared";

interface IncidentTimelineProps {
  incident: Incident;
  updates: IncidentUpdate[];
}

const STATUS_LABELS: Record<string, string> = {
  investigating: "Investigating",
  identified: "Identified",
  monitoring: "Monitoring",
  resolved: "Resolved",
};

const SEVERITY_COLORS: Record<string, string> = {
  minor: "border-yellow-400 dark:border-yellow-300",
  major: "border-orange-500 dark:border-orange-400",
  critical: "border-red-600 dark:border-red-500",
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function IncidentTimeline({ incident, updates }: IncidentTimelineProps) {
  const severityColor = SEVERITY_COLORS[incident.severity] ?? "border-gray-300 dark:border-slate-600";

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className={`border-l-4 ${severityColor} pl-3`}>
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{incident.title}</h3>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {formatDateTime(incident.started_at)}
          {incident.resolved_at && ` — ${formatDateTime(incident.resolved_at)}`}
        </p>
        {incident.body && (
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{incident.body}</p>
        )}
      </div>

      {updates.length > 0 && (
        <div className="mt-4 space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Updates
          </h4>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-3 top-0 h-full w-0.5 bg-gray-200 dark:bg-slate-700" />

            <div className="space-y-4">
              {updates.map((update) => (
                <div key={update.id} className="relative pl-10">
                  {/* Dot */}
                  <div className="absolute left-2 top-1 h-2.5 w-2.5 rounded-full border-2 border-blue-500 bg-white dark:border-blue-400 dark:bg-slate-900" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {STATUS_LABELS[update.status] ?? update.status} —{" "}
                      {formatDateTime(update.created_at)}
                    </p>
                    <p className="mt-1 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                      {update.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
